/**
 * The country pages at `/proxy-locations/:slug`: what a country is made of,
 * where its page lives, and how much of the order each list shows.
 *
 * The countries are the CMS's, as Webflow's Proxy Locations collection kept
 * them (`proxyLocation`): each one's IP count, flag and the words of its own
 * page, in the order `locationOrder` sets, and the product cards those pages
 * list (`proxyProduct`, Webflow's Proxy Types). They are read while a page
 * renders (`src/content/locationSource.ts`), so a count or a sentence changed
 * in the Studio reaches every page that prints it without a deploy. What every
 * country page says the same way, and how it is drawn, is code
 * (`src/routes/proxy-locations/-components/locationContent.ts`).
 *
 * `src/content/prerenderPages.ts` imports this module, and `vite.config.mts`
 * imports that one by relative path and lets esbuild inline it into the bundled
 * config. So nothing may be imported here but types, which esbuild drops: not a
 * `@/...` specifier, which would survive the inlining as a bare import Node
 * cannot resolve, and not a relative one that drags the content layer into the
 * build's config pass.
 */
import type { FaqAnswer } from "../components/marketing/faqAnswer";

/** A product card a country page lists. */
export type ProxyProduct = {
  title: string;
  /** The glyph the card opens on. */
  icon: string;
  summary: string;
  benefits: Array<string>;
  /** The words before the price: "Starts from 250GB for". */
  caption: string;
  price: string;
  /** After the price: "/Proxy" or "/GB". */
  unit: string;
  /** The card's button: what it says, the checkout it opens, and where. */
  button: { label: string; href: string; newTab: boolean };
};

/** One question on a country's page, in the shape every marketing FAQ is written in. */
export type LocationQuestion = { question: string; answer: FaqAnswer };

/**
 * A country as the lists print it: the home page's and pricing's eight, and a
 * country page's ten and then all of them.
 */
export type LocationListItem = {
  /** The two-letter path segment, as the sitemap publishes it. */
  slug: string;
  /** The label every list and the hero's count line carry. */
  name: string;
  /** Online IPs, formatted as the site prints it. */
  ipCount: string;
  /** The flag's address. */
  flag: string;
};

/** A country and the words of its own page, each as the CMS holds it. */
export type ProxyLocation = LocationListItem & {
  /**
   * How the page's `<title>` and meta description name the country, where the
   * site publishes a shorter form: "USA" against "United States". The body
   * copy does not follow it.
   */
  titleName?: string;
  /** The `h1`: "Germany Proxy". */
  heading: string;
  /** The product cards the page lists, in its order. */
  products: Array<ProxyProduct>;
  proxyListHeading: string;
  proxyListText: string;
  priceHeading: string;
  extensionHeading: string;
  extensionText: string;
  faq: Array<LocationQuestion>;
};

/** How many of the order's head the home page and pricing list. */
export const TOP_COUNT = 8;

/**
 * How many of the order's head the "Most Popular Proxy Locations" block shows.
 * The published block is the first ten of the same order.
 */
export const POPULAR_COUNT = 10;

/** The id the "See All Locations" control names and the full list carries. */
export const ALL_LOCATIONS_ID = "all-locations";

const PROXY_LOCATIONS_BASE_PATH = "/proxy-locations";

/** Where a country's page lives. Also what `prerenderPages.ts` enumerates. */
export function proxyLocationPath(slug: string): string {
  return `${PROXY_LOCATIONS_BASE_PATH}/${slug}`;
}

/** The form the `<title>` and meta description use: the title name where there is one. */
export function headlineName(location: ProxyLocation): string {
  return location.titleName ?? location.name;
}

/** A country as the lists print it, without the words of its page. */
export function listItem(location: LocationListItem): LocationListItem {
  return {
    slug: location.slug,
    name: location.name,
    ipCount: location.ipCount,
    flag: location.flag,
  };
}
